import React, { useState } from 'react';
import {
  HomeIcon,
  ShieldIcon,
  DollarIcon,
  ChatIcon,
} from './Illustrations';

function PropertySummary({ data }) {
  if (!data) return null;
  return (
    <section className="result-section property-summary">
      <h2 className="section-title">
        <HomeIcon />
        Property Summary
      </h2>
      <div className="summary-card">
        <div className="summary-address">
          <span className="label">Address</span>
          <span className="value">{data.fullAddress || 'Not specified'}</span>
        </div>
        <div className="summary-meta">
          <div className="meta-item">
            <span className="label">Property Type</span>
            <span className="property-type-badge">
              {data.propertyType || 'Unknown'}
            </span>
          </div>
        </div>
        {data.description && (
          <p className="summary-description">{data.description}</p>
        )}
      </div>
    </section>
  );
}

function FeesSection({ fees }) {
  const [openNotes, setOpenNotes] = useState(new Set());

  function toggleNote(i) {
    setOpenNotes((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  }

  if (!fees || fees.length === 0) {
    return (
      <section className="result-section">
        <h2 className="section-title">
          <DollarIcon />
          All Fees Included
        </h2>
        <div className="empty-state">
          <p>No recurring fees were identified in the document.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="result-section">
      <h2 className="section-title">
        <DollarIcon />
        All Fees Included
      </h2>
      <p className="section-subtitle">
        These are ongoing costs you'll need to budget for as the property owner.
      </p>
      <div className="fees-table-wrapper">
        <table className="fees-table">
          <thead>
            <tr>
              <th>Fee</th>
              <th>Amount</th>
              <th>Frequency</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {fees.map((fee, i) => (
              <React.Fragment key={i}>
                <tr>
                  <td className="fee-name">{fee.name}</td>
                  <td className="fee-amount">{fee.amount || 'N/A'}</td>
                  <td>
                    <span className="frequency-badge">
                      {fee.frequency || 'N/A'}
                    </span>
                  </td>
                  <td className="fee-notes-cell">
                    {fee.notes && fee.notes !== '—' && (
                      <button
                        className="notes-toggle"
                        onClick={() => toggleNote(i)}
                        aria-expanded={openNotes.has(i)}
                      >
                        Notes {openNotes.has(i) ? '▲' : '▼'}
                      </button>
                    )}
                  </td>
                </tr>
                {openNotes.has(i) && fee.notes && (
                  <tr className="fee-notes-row">
                    <td colSpan={4}>
                      <span className="fee-notes-text">{fee.notes}</span>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const EASEMENT_COLORS = {
  drainage: '#3b82f6',
  sewerage: '#8b5cf6',
  power: '#f59e0b',
  access: '#ef4444',
  gas: '#f97316',
  carriageway: '#ec4899',
  other: '#6b7280',
};

function easementLabel(e) {
  const summary = e.description || `${e.type} easement`;
  const prefix = e.reference ? `${e.reference} ` : '';
  const width = e.width ? ` ${e.width} wide.` : '';
  return `${prefix}${summary.replace(/\.\s*$/, '')}.${width}`;
}

function EasementLegend({ easements, style }) {
  const [openNotes, setOpenNotes] = useState(new Set());

  function toggleNote(i) {
    setOpenNotes((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  }

  return (
    <div className="easement-legend" style={style}>
      {easements.map((e, i) => (
        <div key={i} className="easement-legend-entry">
          <div className="easement-legend-item">
            <span
              className="easement-legend-dot"
              style={{ background: EASEMENT_COLORS[e.type] || EASEMENT_COLORS.other }}
            />
            <span>{easementLabel(e)}</span>
            {e.notes && (
              <button
                className="notes-toggle"
                onClick={() => toggleNote(i)}
                aria-expanded={openNotes.has(i)}
              >
                Notes {openNotes.has(i) ? '▲' : '▼'}
              </button>
            )}
          </div>
          {openNotes.has(i) && e.notes && (
            <p className="easement-legend-notes">{e.notes}</p>
          )}
        </div>
      ))}
    </div>
  );
}

function easementStrip(position, index, landX, landY, landW, landH) {
  const thickness = 22;
  const offset = index * 4;
  switch (position) {
    case 'front':
      return { x: landX, y: landY + offset, width: landW, height: thickness };
    case 'rear':
      return { x: landX, y: landY + landH - thickness - offset, width: landW, height: thickness };
    case 'left':
      return { x: landX + offset, y: landY, width: thickness, height: landH };
    case 'right':
      return { x: landX + landW - thickness - offset, y: landY, width: thickness, height: landH };
    case 'diagonal':
      return null;
    default:
      return { x: landX + landW / 2 - thickness / 2, y: landY, width: thickness, height: landH };
  }
}

function EasementMap({ easements, landDetails, planImage, planImages, propertyType }) {
  const hasEasements = easements && easements.length > 0;

  const lotNumRaw = landDetails?.lotNumber?.match(/\bLot\s+(\w+)\b/i)?.[1];
  const isStrataLot = lotNumRaw && /^\d+[A-Za-z]\d+$/i.test(lotNumRaw);

  const sectionTitle = (
    <h2 className="section-title">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M3 9h18M9 21V9" />
      </svg>
      Property Land &amp; Easements
    </h2>
  );

  // Multi-level apartment plans are too complex to map usefully — the server
  // skips rendering them. Say so instead of silently omitting the section,
  // and still list any easements disclosed in the document.
  if (propertyType === 'apartment') {
    return (
      <section className="result-section">
        {sectionTitle}
        <div className="strata-lot-notice">
          <strong>Easement &amp; plan imagery isn&rsquo;t available for apartments</strong>
          <p>
            Apartment (strata) plans span multiple building levels, so we can&rsquo;t reliably
            highlight your lot and its easements on a single diagram.
            {landDetails?.lotNumber && (
              <> Your lot is <span className="strata-lot-badge">{landDetails.lotNumber}</span>.</>
            )}{' '}
            To view the plan itself, open your Section 32 document and locate the Plan of
            Subdivision pages. Any easements disclosed in the document are listed below.
          </p>
        </div>
        {hasEasements ? (
          <EasementLegend easements={easements} style={{ marginTop: '0.5rem' }} />
        ) : (
          <p className="easement-empty">No easements or encumbrances were identified in this document.</p>
        )}
      </section>
    );
  }

  const images = planImages && planImages.length > 0 ? planImages : planImage ? [planImage] : null;

  if (images) {
    return (
      <section className="result-section">
        {sectionTitle}
        {!hasEasements && (
          <p className="easement-empty">No easements or encumbrances were identified in this document.</p>
        )}
        {landDetails?.lotNumber && (
          <p className="plan-lot-label">
            <span className="plan-lot-badge">{landDetails.lotNumber}</span>
          </p>
        )}
        {images.map((img, i) => (
          <div key={i} className="plan-image-wrapper">
            <img
              src={`data:image/png;base64,${img}`}
              alt={`Plan of subdivision — diagram ${i + 1} of ${images.length}`}
              className="plan-image"
            />
          </div>
        ))}
        {hasEasements && <EasementLegend easements={easements} />}
      </section>
    );
  }

  // Fallback: no plan image available
  const svgW = 480;
  const svgH = 340;
  const padX = 60;
  const padY = 50;
  const landW = svgW - padX * 2;
  const landH = svgH - padY * 2 - 30;

  return (
    <section className="result-section">
      {sectionTitle}
      {!hasEasements && (
        <p className="easement-empty">No easements or encumbrances were identified in this document.</p>
      )}
      {isStrataLot && (
        <div className="strata-lot-notice">
          <strong>Unable to retrieve floorplan for apartments</strong>
          <p>
            Your unit is <span className="strata-lot-badge">{landDetails.lotNumber}</span>.
            To view your floor plan, open your Section 32 document, locate the Plan of Subdivision,
            and find <strong>{lotNumRaw}</strong> on the strata plan pages.
          </p>
        </div>
      )}
      {hasEasements && isStrataLot && (
        <EasementLegend easements={easements} style={{ marginTop: '0.5rem' }} />
      )}
      {!isStrataLot && <div className="easement-map-wrapper">
        <svg
          viewBox={`0 0 ${svgW} ${svgH}`}
          className="easement-map-svg"
          role="img"
          aria-label="Property land diagram with easements"
        >
          <rect width={svgW} height={svgH} fill="#f1f5f9" />
          <rect x={0} y={0} width={svgW} height={padY - 8} fill="#cbd5e1" />
          <text x={svgW / 2} y={padY - 18} textAnchor="middle" fontSize="11" fill="#475569" fontFamily="sans-serif">STREET</text>
          <line x1={0} x2={svgW} y1={padY - 8} y2={padY - 8} stroke="#94a3b8" strokeWidth="2" />
          <rect x={padX} y={padY} width={landW} height={landH} fill="#d1fae5" stroke="#059669" strokeWidth="2.5" />
          {hasEasements && easements.map((e, i) => {
            const color = EASEMENT_COLORS[e.type] || EASEMENT_COLORS.other;
            const strip = easementStrip(e.position, i, padX, padY, landW, landH);
            if (!strip) {
              return (
                <line key={i} x1={padX} y1={padY} x2={padX + landW} y2={padY + landH}
                  stroke={color} strokeWidth="18" strokeOpacity="0.45" />
              );
            }
            return (
              <g key={i}>
                <rect x={strip.x} y={strip.y} width={strip.width} height={strip.height}
                  fill={color} fillOpacity="0.38" stroke={color} strokeWidth="1.5" strokeOpacity="0.7" />
                <text x={strip.x + strip.width / 2} y={strip.y + strip.height / 2 + 4}
                  textAnchor="middle" fontSize="9" fill={color} fontWeight="600" fontFamily="sans-serif">
                  {e.type.toUpperCase()}
                </text>
              </g>
            );
          })}
          <text x={padX + landW / 2} y={padY + landH / 2} textAnchor="middle" fontSize="13"
            fill="#065f46" fontWeight="700" fontFamily="sans-serif">
            {landDetails?.lotNumber || 'LOT'}
          </text>
          {landDetails?.area && (
            <text x={padX + landW / 2} y={padY + landH / 2 + 18} textAnchor="middle"
              fontSize="11" fill="#059669" fontFamily="sans-serif">{landDetails.area}</text>
          )}
          {landDetails?.frontage && (
            <text x={padX + landW / 2} y={padY - 4} textAnchor="middle" fontSize="10" fill="#475569" fontFamily="sans-serif">
              {landDetails.frontage}
            </text>
          )}
          {landDetails?.depth && (
            <text x={padX - 6} y={padY + landH / 2} textAnchor="middle" fontSize="10" fill="#475569"
              fontFamily="sans-serif" transform={`rotate(-90, ${padX - 6}, ${padY + landH / 2})`}>
              {landDetails.depth}
            </text>
          )}
          <g transform={`translate(${svgW - 30}, ${padY + 20})`}>
            <circle r="14" fill="white" stroke="#cbd5e1" strokeWidth="1" />
            <text textAnchor="middle" y="-3" fontSize="10" fontWeight="700" fill="#1e293b" fontFamily="sans-serif">N</text>
            <line x1="0" y1="-10" x2="0" y2="10" stroke="#94a3b8" strokeWidth="1" />
            <line x1="-10" y1="0" x2="10" y2="0" stroke="#94a3b8" strokeWidth="1" />
          </g>
          <rect x={padX} y={padY + landH} width={landW} height={svgH - padY - landH - 2} fill="#e2e8f0" stroke="#cbd5e1" strokeWidth="1" />
          <text x={padX + landW / 2} y={svgH - 8} textAnchor="middle" fontSize="10" fill="#94a3b8" fontFamily="sans-serif">NEIGHBOURING PROPERTY</text>
        </svg>
        {hasEasements && <EasementLegend easements={easements} />}
      </div>}
    </section>
  );
}

function RiskCard({ risk }) {
  const [open, setOpen] = useState(false);
  const tierClass = `risk-${risk.tier}`;
  return (
    <div className={`risk-card ${tierClass}`}>
      <div className="risk-header">
        <span className={`risk-badge ${tierClass}`}>
          {risk.tier.toUpperCase()}
        </span>
        <h4 className="risk-title">{risk.issue}</h4>
        {risk.details && (
          <button
            className="notes-toggle"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
          >
            Notes {open ? '▲' : '▼'}
          </button>
        )}
      </div>
      {open && <p className="risk-details">{risk.details}</p>}
    </div>
  );
}

const EASEMENT_KEYWORDS = /easement|right.of.way|carriageway|drainage.easement|sewer.easement/i;

function RisksSection({ risks }) {
  risks = (risks || []).filter(r => !EASEMENT_KEYWORDS.test(r.issue) && !EASEMENT_KEYWORDS.test(r.details || ''));
  if (!risks || risks.length === 0) {
    return (
      <section className="result-section">
        <h2 className="section-title">
          <ShieldIcon />
          Risks &amp; Issues
        </h2>
        <div className="empty-state">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '0.5rem' }}>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <polyline points="9 12 12 15 16 10" />
          </svg>
          <p>No significant risks or issues identified. This is a positive sign, but always have a conveyancer review the full document.</p>
        </div>
      </section>
    );
  }

  const high = risks.filter((r) => r.tier === 'high');
  const medium = risks.filter((r) => r.tier === 'medium');
  const low = risks.filter((r) => r.tier === 'low');

  return (
    <section className="result-section">
      <h2 className="section-title">
        <ShieldIcon />
        Risks &amp; Issues
        <span className="risk-count">
          {risks.length} found
        </span>
      </h2>
      <div className="risk-summary-bar">
        {high.length > 0 && (
          <span className="risk-summary-chip risk-high">{high.length} High</span>
        )}
        {medium.length > 0 && (
          <span className="risk-summary-chip risk-medium">{medium.length} Medium</span>
        )}
        {low.length > 0 && (
          <span className="risk-summary-chip risk-low">{low.length} Low</span>
        )}
      </div>
      {high.length > 0 && (
        <div className="risk-tier-notice risk-tier-high">
          <strong>Action Required:</strong> High-risk items need attention before making an offer. Discuss these with your conveyancer.
        </div>
      )}
      <div className="risks-grid">
        {[...high, ...medium, ...low].map((risk, i) => (
          <RiskCard key={i} risk={risk} />
        ))}
      </div>
    </section>
  );
}

function QuestionCard({ q, index }) {
  const [open, setOpen] = useState(false);
  const text = typeof q === 'string' ? q : q.question;
  const why = typeof q === 'string' ? null : q.why;
  return (
    <div className="question-card">
      <div className="question-row">
        <span className="question-number">{index + 1}</span>
        <span className="question-text">{text}</span>
        {why && (
          <button
            className="notes-toggle"
            onClick={() => setOpen(o => !o)}
            aria-expanded={open}
          >
            Why {open ? '▲' : '▼'}
          </button>
        )}
      </div>
      {open && why && (
        <p className="question-why">{why}</p>
      )}
    </div>
  );
}

function QuestionsSection({ questions }) {
  if (!questions || questions.length === 0) return null;

  return (
    <section className="result-section">
      <h2 className="section-title">
        <ChatIcon />
        Questions for Your Agent
      </h2>
      <p className="section-subtitle">
        Ask your real estate agent or conveyancer these questions before making an offer.
      </p>
      <div className="questions-list">
        {questions.map((q, i) => (
          <QuestionCard key={i} q={q} index={i} />
        ))}
      </div>
    </section>
  );
}

export default function AnalysisResults({ data, onReset }) {
  const { analysis, filename, meta, planImage, planImages } = data;

  const risks = (analysis.risks || []).filter(
    (r) => !EASEMENT_KEYWORDS.test(r.issue) && !EASEMENT_KEYWORDS.test(r.details || '')
  );
  const highRisks = risks.filter((r) => r.tier === 'high').length;
  const feeCount = (analysis.fees || []).length;
  const questionCount = (analysis.agentQuestions || []).length;
  const propertyType = analysis.propertySummary?.propertyType;

  return (
    <div className="results-container">
      <header className="results-hero">
        <div className="results-hero-top">
          <span className="results-eyebrow">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <polyline points="8.5 12.5 11 15 15.5 9.5" />
            </svg>
            Analysis complete
          </span>
          <button className="btn btn-primary" onClick={onReset}>
            Analyse another document
          </button>
        </div>
        <h1 className="results-address">
          {analysis.propertySummary?.fullAddress || 'Your property report'}
        </h1>
        <div className="results-chips">
          <span className="results-chip">{filename}</span>
          {propertyType && (
            <span className="results-chip results-chip-type">{propertyType}</span>
          )}
        </div>
      </header>

      <div className="results-overview">
        <div className="stat-tile">
          <span className="stat-num">{risks.length}</span>
          <span className="stat-label">Risks flagged</span>
          {risks.length === 0 ? (
            <span className="stat-sub stat-sub-ok">None identified</span>
          ) : highRisks > 0 ? (
            <span className="stat-sub stat-sub-high">{highRisks} high priority</span>
          ) : (
            <span className="stat-sub stat-sub-ok">No high-priority items</span>
          )}
        </div>
        <div className="stat-tile">
          <span className="stat-num">{feeCount}</span>
          <span className="stat-label">Ongoing fees</span>
        </div>
        <div className="stat-tile">
          <span className="stat-num">{questionCount}</span>
          <span className="stat-label">Questions to ask</span>
        </div>
      </div>

      <div className="results-disclaimer">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7A6248" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '2px' }}>
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <p>
          This is an AI-generated summary for informational purposes only. It is not legal advice.
          Always have a qualified conveyancer or solicitor review the full Section 32/Contract of Sale before signing.
        </p>
      </div>

      <PropertySummary data={analysis.propertySummary} />
      <EasementMap
        easements={analysis.easements}
        landDetails={analysis.landDetails}
        planImage={planImage}
        planImages={planImages}
        propertyType={analysis.propertySummary?.propertyType}
      />
      <FeesSection fees={analysis.fees} />
      <RisksSection risks={analysis.risks} />
      <QuestionsSection questions={analysis.agentQuestions} />

      {meta && (
        <div className="meta-info">
          <span>Processed in {(meta.processingTime / 1000).toFixed(1)}s</span>
          <span>Model: {meta.model}</span>
          {meta.inputTokens && (
            <span>Tokens: {meta.inputTokens.toLocaleString()} in / {meta.outputTokens.toLocaleString()} out</span>
          )}
        </div>
      )}
    </div>
  );
}
